/**
 * The demo listings store: one per tenant fixture, held for the life of the
 * `MockApiClient` instance so a create/edit/generate/approve/publish session
 * survives navigation. Pacing and sell-through are computed fresh from the
 * same `DemoDataset` the Overview and Analytics pages use, from whichever
 * tenant competition a listing is paired with, so the figures never disagree
 * (pivot doc §6 Stage 3: "generators/scrapers stubbed" but domain models and
 * figures are real).
 */

import {
  allFieldGroupsApproved,
  PUBLISH_TARGETS,
  type FieldGroupKey,
  type Listing,
  type ListingApprovals,
  type ListingInput,
  type ListingStatus,
  type ListingTemplate,
  type ListingTemplateInput,
  type ListingTemplatePrize,
  type PublishLogEntry,
  type PublishTargetKey,
  type PublishTargetState,
} from '@/api/listings'
import type { EntityRef } from '@/lib/drilldown'

import type { DemoDataset } from '../generators/engine'
import { mockListingContentGenerator } from './generator'
import { LISTING_TEMPLATES } from './fixtures'
import { hashString } from '../random'
import type { CompetitionFixture, TenantFixture } from '../tenants/types'

function emptyApprovals(): ListingApprovals {
  return {
    prizeDetails: { approved: false, approvedAt: null },
    copy: { approved: false, approvedAt: null },
  }
}

function emptyPublishing(): PublishTargetState[] {
  return PUBLISH_TARGETS.map((target) => ({ target, status: 'not_published', publishedAt: null, stale: false }))
}

function deriveStatus(listing: Pick<Listing, 'copy' | 'approvals' | 'publishing'>): ListingStatus {
  // Approval is checked before publish state on purpose: a listing that was published and
  // then edited or regenerated is no longer fully approved, and must say so rather than keep
  // reporting 'published' for content nobody has reviewed. Re-approving alone is not enough
  // either: a target stays stale until it is actually published again.
  const approved = allFieldGroupsApproved(listing.approvals)
  const published = listing.publishing.filter((target) => target.status === 'published')
  if (published.length > 0) {
    return approved && published.every((target) => !target.stale) ? 'published' : 'needs_republish'
  }
  if (approved) return 'approved'
  if (listing.copy) return 'content_drafted'
  return 'draft'
}

/** Mark every published target stale — call whenever a field-group approval is reset. */
function markPublishedStale(publishing: Listing['publishing']): Listing['publishing'] {
  return publishing.map((state) => (state.status === 'published' ? { ...state, stale: true } : state))
}

/** Key-order-independent structural equality, so a form round-trip is not mistaken for an edit. */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`
  }
  return JSON.stringify(value)
}

function fromFixture(fixture: CompetitionFixture): ListingTemplatePrize {
  const seed = hashString(fixture.id)
  return {
    prizeName: fixture.name,
    prizeCategory: fixture.category,
    prizeValue: Math.round(fixture.ticketPrice * fixture.maxEntries * 0.35),
    ticketPrice: fixture.ticketPrice,
    maxEntries: fixture.maxEntries,
    instantWinsCount: seed % 4,
    mainImageUrl: '',
    additionalMediaUrls: [],
    keySellingPoints: [`Win ${fixture.name}`, 'Instant wins available', 'Winner drawn live'].slice(0, 2 + (seed % 2)),
    prizeSpecifications: 'Prize delivered or paid out within 14 days of the draw.',
  }
}

const SEED_STATUS_CYCLE: readonly ListingStatus[] = ['published', 'approved', 'content_drafted', 'draft']

export class ListingsStore {
  private nextSequence = 1
  private readonly listings: Listing[]
  private templates: ListingTemplate[] = [...LISTING_TEMPLATES]

  constructor(
    private readonly tenant: TenantFixture,
    now: Date,
  ) {
    this.listings = this.seed(now)
  }

  private id(prefix: string): string {
    const value = `${prefix}_${this.nextSequence.toString(36)}`
    this.nextSequence += 1
    return value
  }

  private entityFor(competitionId: string | null): EntityRef | null {
    if (!competitionId) return null
    const fixture = this.tenant.competitions.find((competition) => competition.id === competitionId)
    return fixture ? { type: 'competition', id: fixture.id, label: fixture.name } : null
  }

  private seed(now: Date): Listing[] {
    const iso = now.toISOString()
    const seeded = this.tenant.competitions.slice(0, 4).map((fixture, index): Listing => {
      const targetStatus = SEED_STATUS_CYCLE[index % SEED_STATUS_CYCLE.length] as ListingStatus
      const prize = { ...fromFixture(fixture), drawDate: drawDateFor(fixture, now) }
      const copy = targetStatus === 'draft' ? null : mockListingContentGenerator.generate(prize, this.tenant.name)
      const approvals = emptyApprovals()
      if (targetStatus === 'approved' || targetStatus === 'published') {
        approvals.prizeDetails = { approved: true, approvedAt: iso }
        approvals.copy = { approved: true, approvedAt: iso }
      }
      const publishing = emptyPublishing()
      const publishLog: PublishLogEntry[] = []
      if (targetStatus === 'published') {
        for (const state of publishing) {
          state.status = 'published'
          state.publishedAt = iso
          publishLog.push({
            id: this.id('log'),
            listingId: fixture.id,
            listingTitle: copy?.title ?? prize.prizeName,
            target: state.target,
            status: 'published',
            message: publishMessage(state.target),
            at: iso,
          })
        }
      }
      return {
        id: `lst_${fixture.id}`,
        status: deriveStatus({ copy, approvals, publishing }),
        prize,
        copy,
        approvals,
        publishing,
        publishLog,
        competitionEntity: this.entityFor(fixture.id),
        pacing: null,
        sellThrough: null,
        createdAt: iso,
        updatedAt: iso,
      }
    })
    return seeded
  }

  private withComputed(listing: Listing, dataset: DemoDataset): Listing {
    if (!listing.competitionEntity) return listing
    const fixture = this.tenant.competitions.find((competition) => competition.id === listing.competitionEntity?.id)
    if (!fixture) return listing
    const status = dataset.competitionStatus(fixture)
    return { ...listing, pacing: status.pacing, sellThrough: status.sellThrough }
  }

  list(dataset: DemoDataset): Listing[] {
    return this.listings
      .map((listing) => this.withComputed(listing, dataset))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id))
  }

  get(id: string, dataset: DemoDataset): Listing | null {
    const listing = this.listings.find((candidate) => candidate.id === id)
    return listing ? this.withComputed(listing, dataset) : null
  }

  create(input: ListingInput, now: Date): Listing {
    const iso = now.toISOString()
    const listing: Listing = {
      id: this.id('lst'),
      status: 'draft',
      prize: input.prize,
      copy: null,
      approvals: emptyApprovals(),
      publishing: emptyPublishing(),
      publishLog: [],
      competitionEntity: this.entityFor(input.competitionId),
      pacing: null,
      sellThrough: null,
      createdAt: iso,
      updatedAt: iso,
    }
    this.listings.push(listing)
    return listing
  }

  private require(id: string): Listing {
    const listing = this.listings.find((candidate) => candidate.id === id)
    if (!listing) throw new Error(`Listing not found: ${id}`)
    return listing
  }

  private replace(next: Listing): Listing {
    const index = this.listings.findIndex((candidate) => candidate.id === next.id)
    this.listings[index] = next
    return next
  }

  update(id: string, input: ListingInput, now: Date): Listing {
    const current = this.require(id)
    const prizeChanged = canonical(current.prize) !== canonical(input.prize)
    const approvals: ListingApprovals = prizeChanged
      ? { ...current.approvals, prizeDetails: { approved: false, approvedAt: null } }
      : current.approvals
    const next: Listing = {
      ...current,
      prize: input.prize,
      approvals,
      publishing: prizeChanged ? markPublishedStale(current.publishing) : current.publishing,
      competitionEntity: this.entityFor(input.competitionId),
      updatedAt: now.toISOString(),
    }
    // Edited prize details need re-approval, same as a regenerated draft needs re-approval —
    // otherwise an already-approved listing could publish unreviewed edits.
    next.status = deriveStatus(next)
    return this.replace(next)
  }

  generateContent(id: string, now: Date): Listing {
    const current = this.require(id)
    const copy = mockListingContentGenerator.generate(current.prize, this.tenant.name)
    const approvals: ListingApprovals = {
      ...current.approvals,
      // A fresh draft needs a fresh copy review, even if it was approved before.
      copy: { approved: false, approvedAt: null },
    }
    const next: Listing = { ...current, copy, approvals, publishing: markPublishedStale(current.publishing), updatedAt: now.toISOString() }
    next.status = deriveStatus(next)
    return this.replace(next)
  }

  setApproval(id: string, group: FieldGroupKey, approved: boolean, now: Date): Listing {
    const current = this.require(id)
    const approvals: ListingApprovals = {
      ...current.approvals,
      [group]: { approved, approvedAt: approved ? now.toISOString() : null },
    }
    const next: Listing = { ...current, approvals, updatedAt: now.toISOString() }
    next.status = deriveStatus(next)
    return this.replace(next)
  }

  publish(id: string, target: PublishTargetKey, now: Date): Listing {
    const current = this.require(id)
    if (!allFieldGroupsApproved(current.approvals)) throw new Error('Approve every field group before publishing.')

    const iso = now.toISOString()
    const publishing = current.publishing.map((state) =>
      state.target === target ? { ...state, status: 'published' as const, publishedAt: iso, stale: false } : state,
    )
    const title = current.copy?.title ?? current.prize.prizeName
    const entry: PublishLogEntry = {
      id: this.id('log'),
      listingId: current.id,
      listingTitle: title,
      target,
      status: 'published',
      message: publishMessage(target),
      at: iso,
    }
    const next: Listing = {
      ...current,
      publishing,
      publishLog: [entry, ...current.publishLog],
      updatedAt: iso,
    }
    next.status = deriveStatus(next)
    return this.replace(next)
  }

  listTemplates(): ListingTemplate[] {
    return this.templates
  }

  saveTemplate(input: ListingTemplateInput): ListingTemplate {
    const template: ListingTemplate = {
      id: `tpl_${this.id('custom')}`,
      name: input.name,
      description: input.description,
      prize: input.prize,
    }
    this.templates = [...this.templates, template]
    return template
  }
}

function drawDateFor(fixture: CompetitionFixture, now: Date): string {
  const opens = new Date(now)
  opens.setUTCDate(opens.getUTCDate() + fixture.opensInDays + fixture.durationDays - 1)
  return opens.toISOString().slice(0, 10)
}

function publishMessage(target: PublishTargetKey): string {
  return target === 'website'
    ? 'Publish intent recorded. The WordPress connector completes the live publish.'
    : `Published to ${target.replace(/_/g, ' ')}.`
}
